import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Building2, UserRound, Sparkles, Megaphone, Filter, Users,
  Library, LogOut, Crown, CreditCard, Ticket, Share2, Rocket, Bot, Gift, Save, Flag, PhoneCall, Leaf, CalendarDays, Clapperboard, GraduationCap, Lightbulb,
} from "lucide-react";

const navItems = [
  { to: "/dashboard/academia", label: "ABC del Multinivel", icon: GraduationCap },
  { to: "/dashboard", label: "Inicio", icon: LayoutDashboard, end: true },
  { to: "/dashboard/antes-de-empezar", label: "Antes de empezar", icon: Rocket },
  { to: "/dashboard/perfil", label: "Perfil del Líder", icon: UserRound },
  { to: "/dashboard/empresas", label: "Empresas", icon: Building2 },
  { to: "/dashboard/generador", label: "Generador IA", icon: Sparkles },
  { to: "/dashboard/campanas", label: "Campañas", icon: Megaphone },
  { to: "/dashboard/embudos", label: "Embudos", icon: Filter },
  { to: "/dashboard/redes", label: "Redes Sociales", icon: Share2 },
  { to: "/dashboard/publicaciones-gratis", label: "Publicaciones sin costo", icon: Gift },
  { to: "/dashboard/prospectos", label: "Prospectos", icon: Users },
  { to: "/dashboard/minutos-ia", label: "Minutos IA", icon: Bot },
  { to: "/dashboard/llamadas-ia", label: "Llamadas IA", icon: PhoneCall },
  { to: "/dashboard/recursos", label: "Libros y Archivos", icon: Library },
  { to: "/dashboard/planes", label: "Paquetes", icon: CreditCard },
];

export const buildMenuItems = (user) => {
  const canSellPins = (user?.plan === "pro" || user?.plan === "elite") && user?.plan_status === "active";
  const isRenalife = user?.pin_type === "renalife";
  const isLumi = user?.pin_type === "lumi";
  if (isRenalife) {
    return [
      { to: "/dashboard/academia", label: "ABC del Multinivel", icon: GraduationCap },
      navItems[1],
      { to: "/dashboard/materiales-renalife", label: "Materiales RENALIFE", icon: Leaf },
      { to: "/dashboard/multinivel", label: "Campaña MULTINIVEL", icon: Rocket },
      { to: "/dashboard/calendario-renalife", label: "Calendario 15 días", icon: CalendarDays },
      { to: "/dashboard/tiktok-renalife", label: "TikTok con Avatar", icon: Clapperboard },
      { to: "/dashboard/prospectos", label: "Prospectos", icon: Users },
      { to: "/dashboard/perfil", label: "Mi WhatsApp y Perfil", icon: UserRound },
      { to: "/dashboard/planes", label: "Paquetes", icon: CreditCard },
    ];
  }
  if (isLumi) {
    return [
      navItems[0],
      { to: "/dashboard/academia-lumi", label: "Academia LUMI", icon: Lightbulb },
      ...navItems.slice(1),
    ];
  }
  return canSellPins
    ? [...navItems, { to: "/dashboard/pins", label: "PINs para Vender", icon: Ticket }]
    : navItems;
};

const GuestBadge = ({ isRenalife, isLumi }) =>
  isLumi ? (
    <div data-testid="lumi-badge" className="mt-3 inline-flex items-center gap-1.5 text-[11px] rounded-full border border-sky-400/50 bg-sky-500/10 px-2.5 py-1 text-sky-300 font-semibold">
      <Lightbulb size={12} /> Distribuidor LUMI
    </div>
  ) : isRenalife ? (
    <div data-testid="renalife-badge" className="mt-3 inline-flex items-center gap-1.5 text-[11px] rounded-full border border-emerald-400/50 bg-emerald-500/10 px-2.5 py-1 text-[#FACC15] font-semibold">
      <Leaf size={12} /> Distribuidor RENALIFE · 1 campaña
    </div>
  ) : (
    <div data-testid="guest-badge" className="mt-3 inline-flex items-center gap-1.5 text-[11px] rounded-full border border-yellow-400/40 bg-yellow-400/10 px-2.5 py-1 text-yellow-impact font-semibold">
      <Ticket size={12} /> Acceso con PIN · 1 campaña
    </div>
  );

const GuestActions = ({ onSaveData, onFinalize }) => (
  <div className="px-3 pb-3 space-y-1.5">
    <button
      data-testid="guest-save-data"
      onClick={onSaveData}
      className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs font-semibold text-green-300 border border-green-500/30 bg-green-500/5 hover:bg-green-500/15 transition-colors"
    >
      <Save size={14} /> Guardar datos para próxima campaña
    </button>
    <button
      data-testid="guest-finalize"
      onClick={onFinalize}
      className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs font-semibold text-zinc-400 border border-white/10 hover:bg-white/5 hover:text-white transition-colors"
    >
      <Flag size={14} /> Finalizar mi campaña
    </button>
  </div>
);

const UserFooter = ({ user, onLogout }) => (
  <div className="p-3 border-t border-white/10">
    <div className="flex items-center gap-3 px-3 py-2 mb-2">
      <div className="w-9 h-9 rounded-full bg-purple-600 flex items-center justify-center font-bold text-sm shrink-0">
        {(user?.name || "L").charAt(0).toUpperCase()}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold truncate">{user?.name || "Líder"}</div>
        <div className="text-xs text-zinc-500 truncate">{user?.phone || user?.email}</div>
      </div>
    </div>
    <button
      data-testid="logout-button"
      onClick={onLogout}
      className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
    >
      <LogOut size={18} /> Cerrar sesión
    </button>
  </div>
);

export const SidebarContent = ({ user, menuItems, onNavigate, onLogout, onSaveData, onFinalize }) => {
  const isGuest = user?.role === "pin_guest";
  const isRenalife = user?.pin_type === "renalife";
  const isLumi = user?.pin_type === "lumi";
  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-6">
        <div className="text-xl font-display font-black tracking-tight">
          MULTI<span className="text-purple-500">CONTACTOS</span>
        </div>
        {isGuest && <GuestBadge isRenalife={isRenalife} isLumi={isLumi} />}
      </div>
      {isGuest && !isLumi && <GuestActions onSaveData={onSaveData} onFinalize={onFinalize} />}
      <nav className="flex-1 px-3 space-y-1">
        {menuItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                isActive
                  ? "bg-purple-600/20 text-white border border-purple-500/40"
                  : "text-zinc-400 hover:text-white hover:bg-white/5 border border-transparent"
              }`
            }
          >
            <item.icon size={18} />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <UserFooter user={user} onLogout={onLogout} />
    </div>
  );
};
